import { Channel, UserResponse } from "stream-chat";
import { BOOKS_CHANNEL_CUSTOM_TYPE } from "constants/channelType";
import {
  ACCOUNT_MANAGER,
  BOOKKEEPER,
  PREPARER,
  REVIEWER,
} from "constants/chatMemberType";
import { fetchAllChannelMembers } from "util/fetchAllChannelMembers";
import { getMentionUserRoles, TAIL_ROLES } from "util/getMentionUserRoles";

export type MentionUser = {
  user: UserResponse;
  roles: string[];
};

type SearchMentionUsersArgs = {
  channel: Channel;
  ownUserId: string | undefined;
  isAdmin: boolean;
  query: string;
};

// Enough to scroll through, without rendering hundreds of rows per keystroke
const MENTION_RESULT_LIMIT = 50;
const ROLE_ORDER = [
  ACCOUNT_MANAGER,
  BOOKKEEPER,
  PREPARER,
  REVIEWER,
  ...TAIL_ROLES,
];

const getRoleRank = ({ roles }: MentionUser): number => {
  const index = roles.length > 0 ? ROLE_ORDER.indexOf(roles[0]) : -1;
  return index === -1 ? ROLE_ORDER.length : index;
};

const isDefinedUser = (user: UserResponse | undefined): user is UserResponse =>
  Boolean(user);

const matchesQuery = (user: UserResponse, normalizedQuery: string): boolean =>
  [user.name ?? "", user.id].some((value) =>
    value.toLowerCase().includes(normalizedQuery)
  );

const getMatchingUsers = async (
  channel: Channel,
  query: string
): Promise<UserResponse[]> => {
  const normalizedQuery = query.toLowerCase();
  const members = await fetchAllChannelMembers(channel);
  const users = [
    ...members.map(({ user }) => user),
    ...Object.values(channel.state?.watchers ?? {}),
  ].filter(isDefinedUser);
  const uniqueUsers = new Map(users.map((user) => [user.id, user]));

  return [...uniqueUsers.values()].filter((user) =>
    matchesQuery(user, normalizedQuery)
  );
};

export const searchChannelMentionUsers = async ({
  channel,
  ownUserId,
  isAdmin,
  query,
}: SearchMentionUsersArgs): Promise<MentionUser[]> => {
  const users = await getMatchingUsers(channel, query);

  // Only admins can see who reviews a filing, except in books channels
  const isBookChannel = channel.data?.custom_type === BOOKS_CHANNEL_CUSTOM_TYPE;
  const hiddenReviewerId =
    isAdmin || isBookChannel
      ? undefined
      : channel.data?.assigned_reviewer_member_id;

  return users
    .filter((user) => user.id !== ownUserId && user.id !== hiddenReviewerId)
    .map((user) => ({ user, roles: getMentionUserRoles(channel, user) }))
    .sort((a, b) => getRoleRank(a) - getRoleRank(b))
    .slice(0, MENTION_RESULT_LIMIT);
};
